import React, { useMemo } from 'react'
import Table from 'antd/lib/table'
import { generatePath, Link } from 'react-router-dom'

import { ROUTES } from 'helpers/constants'
import useQuery from 'hooks/useQuery'
import apiManager from 'helpers/apiManager'
import { dateFormatter } from 'helpers'

import { CloseIcon } from 'components'
import Modal from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'

import { OVERLAY } from 'components/AuthModal/styles'

const getSorter =
  ({ key, formatter = data => data }) =>
  (a, b) =>
    formatter(a[key]) - formatter(b[key])

const getStringSorter =
  ({ key }) =>
  (a, b) =>
    a[key].localeCompare(b[key])

const ProfileLink = ({ id, name }) => {
  const publicProfilePath = useMemo(() => generatePath(ROUTES.ADMIN_USER, { id }), [id])

  return (
    <Link to={publicProfilePath} target="_blank">
      {name}
    </Link>
  )
}

const columns = [
  {
    title: 'First User',
    dataIndex: 'firstName',
    key: 'firstUserId',
    sorter: getStringSorter({ key: 'firstName' }),
    render: (name, { firstUserId }) => <ProfileLink id={firstUserId} name={name} />,
  },
  {
    title: 'Second User',
    dataIndex: 'secondName',
    key: 'secondUserId',
    render: (name, { secondUserId }) => <ProfileLink id={secondUserId} name={name} />,
  },
  {
    title: 'Visible',
    dataIndex: 'visible',
    key: 'visible',
    sorter: getStringSorter({ key: 'visible' }),
  },
  {
    title: 'Revealed',
    dataIndex: 'revealed',
    key: 'revealed',
    sorter: getStringSorter({ key: 'revealed' }),
  },
  {
    title: 'Accepted',
    dataIndex: 'accepted',
    key: 'accepted',
    sorter: getStringSorter({ key: 'accepted' }),
  },
  {
    title: 'Declined',
    dataIndex: 'declined',
    key: 'declined',
    sorter: getStringSorter({ key: 'declined' }),
  },
  {
    title: 'Matched',
    dataIndex: 'matched',
    key: 'matched',
    sorter: getStringSorter({ key: 'matched' }),
  },
  {
    title: 'Purpose',
    dataIndex: 'purpose',
    key: 'purpose',
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: dateFormatter.returnYYYYMMDD,
    sorter: getSorter({ key: 'createdAt', formatter: data => new Date(data) }),
  },
  {
    title: 'Expires At',
    dataIndex: 'expiresAt',
    key: 'expiresAt',
    render: dateFormatter.returnYYYYMMDD,
    sorter: getSorter({ key: 'expiresAt', formatter: data => new Date(data) }),
  },
]

const RecommendationsModal = ({ user, show, onClose }) => {
  const { data } = useQuery('admin/match/recommendations', () =>
    apiManager.adminGetRecommendations(
      {
        userId: user.id,
      },
      {
        enabled: show,
      }
    )
  )
  const recommendations = useMemo(
    () =>
      data?.map(match => ({
        ...match,
        key: match.id,
      })) || [],
    [data]
  )

  return (
    <Overlay show={show} className={OVERLAY} preventEscBackdropClose={true} onClose={onClose}>
      <Modal innerClassName="w-screen max-w-auto px-10 rounded-t-3xl max-w-auto overflow-y-scroll">
        <div className="relative w-full p-5 max-w-full md:p-7 md:py-5 bg-white">
          <button className="bg-transparent text-black absolute top-3 right-4" onClick={onClose}>
            <CloseIcon className="w-4 h-4" />
          </button>
          <div className="text-black text-center text-5xl font-bold font-ownersWide mb-10 underline">
            {user.firstName}'s Recommendations
          </div>
          <div className="overflow-y-scroll" style={{ height: '80vh' }}>
            <Table
              columns={columns}
              dataSource={recommendations}
              pagination={{
                defaultPageSize: 10,
              }}
            />
          </div>
        </div>
      </Modal>
    </Overlay>
  )
}

export default RecommendationsModal
